// src/components/Header.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Phone, Mail, MapPin, ShoppingCart, User, Leaf, Heart, Bell } from 'lucide-react';

const Header = () => {
  const [siteName, setSiteName] = useState('NAVIROSE');
  const [logo, setLogo] = useState('');
  const [contactInfo, setContactInfo] = useState({
    phone: '+92 300 1234567',
    email: 'hello@navirose.com',
    storeLocationUrl: '',
  });
  const [scrolled, setScrolled] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const location = useLocation();

  const { getItemCount } = useCart();
  const { user } = useAuth();
  const cartCount = getItemCount();

  // Fetch site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'site');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.siteName) setSiteName(data.siteName);
          if (data.logo) setLogo(data.logo);
          setContactInfo({
            phone: data.phone || '+92 300 1234567',
            email: data.email || 'hello@navirose.com',
            storeLocationUrl: data.storeLocationUrl || '',
          });
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, [location]);

  // Fetch notification count
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) {
        setNotificationCount(0);
        return;
      }

      try {
        const q = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        const mine = all.filter(n => n.recipients?.includes(user.uid));

        const unread = mine.filter(n => {
          const deletedKey = `notif_deleted_${n.id}_${user.uid}`;
          const seenKey = `notif_seen_${n.id}_${user.uid}`;
          return !localStorage.getItem(deletedKey) && !localStorage.getItem(seenKey);
        });

        setNotificationCount(unread.length);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    fetchNotifications();

    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleStoreLocationClick = () => {
    if (contactInfo.storeLocationUrl) {
      window.open(contactInfo.storeLocationUrl, '_blank');
    }
  };

  return (
    <>
      {/* ================= TOP BAR ================= */}
      <div className="text-xs py-2 px-4"
        style={{ backgroundColor: 'var(--color-topbar-bg)', color: 'var(--color-topbar-text)' }}>
        <div className="max-w-7xl mx-auto flex justify-center md:justify-between items-center">
          <div className="flex flex-wrap justify-center gap-3 md:gap-6">
            <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-1 hover:opacity-80 transition">
              <Phone size={12} /> {contactInfo.phone}
            </a>
            <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-1 hover:opacity-80 transition">
              <Mail size={12} /> {contactInfo.email}
            </a>
            {contactInfo.storeLocationUrl && (
              <button onClick={handleStoreLocationClick} className="hidden md:flex items-center gap-1 hover:opacity-80 transition">
                <MapPin size={12} /> Store Location
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ================= MAIN HEADER ================= */}
      <header className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? 'shadow-lg py-2' : 'shadow-md py-3'}`}
        style={{ backgroundColor: 'var(--color-header-bg)', borderBottom: `1px solid var(--color-border)` }}>
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center gap-4">

          {/* Logo OR Site Name */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            {logo ? (
              <img src={logo} alt={siteName} className="h-10 md:h-12 w-auto object-contain" />
            ) : (
              <>
                <Leaf size={26} className="md:hidden" style={{ color: 'var(--color-dark-pink-text)' }} />
                <Leaf size={30} className="hidden md:block" style={{ color: 'var(--color-dark-pink-text)' }} />
                <span className="text-lg md:text-2xl font-bold" style={{ color: 'var(--color-dark-pink-text)' }}>
                  {siteName}
                </span>
              </>
            )}
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" active={isActive('/')}>Home</NavLink>
            <NavLink to="/shop" active={isActive('/shop')}>Shop</NavLink>
            <NavLink to="/about" active={isActive('/about')}>About</NavLink>
            <NavLink to="/contact" active={isActive('/contact')}>Contact</NavLink>
          </nav>

          {/* Right Icons */}
          <div className="flex items-center gap-3 md:gap-4">

            {/* Wishlist */}
            <Link to="/profile/wishlist"
              className="hidden md:block transition hover:opacity-80"
              style={{ color: 'var(--color-primary-text)' }}
              title="Wishlist">
              <Heart size={22} />
            </Link>

            {/* Notifications */}
            {user && (
              <Link to="/notifications"
                className="relative transition hover:opacity-80"
                style={{ color: 'var(--color-primary-text)' }}
                title="Notifications">
                <Bell size={22} />
                {notificationCount > 0 && (
                  <span className="absolute -top-2 -right-2 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold text-[10px] anim-pulseGlow"
                    style={{ backgroundColor: '#DC829A' }}>
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </span>
                )}
              </Link>
            )}

            {/* Cart */}
            <Link to="/cart"
              className="relative transition hover:opacity-80"
              style={{ color: 'var(--color-primary-text)' }}
              title="Cart">
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold text-[10px] anim-popIn"
                  style={{ backgroundColor: '#DC829A' }}>
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>

            {/* Profile */}
            <Link to="/profile"
              className="hidden md:block transition hover:opacity-80"
              style={{ color: 'var(--color-primary-text)' }}
              title="Profile">
              <User size={22} />
            </Link>
          </div>

        </div>
      </header>
    </>
  );
};

const NavLink = ({ to, active, children }) => (
  <Link to={to}
    className="px-4 py-2 rounded-lg font-medium transition-all duration-200"
    style={{
      backgroundColor: active ? '#F5AEC0' : 'transparent',
      color: active ? '#071B36' : 'var(--color-primary-text)',
      opacity: active ? 1 : 0.85,
      fontWeight: active ? 600 : 500,
    }}
    onMouseEnter={(e) => {
      if (!active) {
        e.currentTarget.style.backgroundColor = '#FFF2F6';
        e.currentTarget.style.opacity = 1;
      }
    }}
    onMouseLeave={(e) => {
      if (!active) {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.opacity = 0.85;
      }
    }}>
    {children}
  </Link>
);

export default Header;